import React, {createRef} from 'react';
import {Task} from './task';
import {ViewMode} from '../CalendarBoxState';
import {MyGantt} from './MyGantt';

export type FrappeGanttProps = {
  tasks: Task[];
  onClick?: (task: Task) => void;
} & Partial<FrappeGanttOptionalProps>;

export type FrappeGanttOptionalProps = Readonly<typeof frappeGanttDefaultProps>;

const frappeGanttDefaultProps = {
  viewMode: ViewMode.Month,
  height: 768,
  setWidth: (w: number) => {
    w.valueOf(); // linter
    return;
  },
  setHeight: (h: number) => {
    h.valueOf(); //linter
    return;
  },
  onViewChange: (mode: ViewMode) => {
    mode.valueOf(); // linter
    return;
  },
};

/* eslint-disable */
export class FrappeGantt extends React.Component<FrappeGanttProps, any> {
  static defaultProps = frappeGanttDefaultProps;
  state = {
    viewMode: null,
    tasks: [
      {
        id: 'Task 1',
        name: 'Redesign GANTT',
        start: '2023-01-03',
        end: '2023-02-4',
        custom_class: 'bar-milestone', // optional
      },
    ],
  };

  private _target = createRef<HTMLDivElement>();
  private _svg = createRef<SVGSVGElement>();
  private _gantt: any = null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static getDerivedStateFromProps(nextProps: FrappeGanttProps, state: any) {
    return {
      viewMode: nextProps.viewMode,
      tasks: nextProps.tasks.map((t) => new Task(t)),
    };
  }

  protected handleResize(): void {
    if (!this.props.setWidth || !this.props.setHeight) {
      return;
    }
    const h = window.innerHeight;
    const w = window.innerWidth / 1.62;
    const aspect = w / h;

    const adjustedHeight = Math.ceil(w / aspect);
    this.props.setWidth(w);
    this.props.setHeight(adjustedHeight);
  }

  protected setSVGSize(): void {
    this._svg.current?.setAttribute('height', this.props.height + '');
    this._svg.current?.setAttribute('viewBox', `0 0 1400 ${this.props.height}`);
    this._svg.current?.setAttribute('preserveAspectRatio', `xMinYMin slice`);
  }

  protected changeMonthsNames(): void {
    const monthList =
      this.state.viewMode === ViewMode.Day
        ? this._svg.current?.getElementsByClassName('upper-text')
        : this._svg.current?.getElementsByClassName('lower-text');
    if (!monthList) {
      return;
    }
    for (let m of Array.from(monthList)) {
      if (m.textContent) {
        const format = new Intl.DateTimeFormat('en', {month: 'long'});
        const month = format.format(new Date(Date.parse(m.textContent + ' 1, 2012')));
        m.textContent = month[0].toUpperCase() + month.slice(1);
      }
    }
  }

  protected changeCalendarLayout() {
    if (this.state.viewMode === ViewMode.Day) {
      const days = this._svg.current?.getElementsByClassName('thick tick');
      if (!days) {
        return;
      }
      for (let m of Array.from(days)) {
        m.setAttribute('class', 'thick tick bold');
        m.setAttribute('M', 'v 900');
      }
    }
    if (this.state.viewMode === ViewMode.Day) {
      const day = this._svg.current?.getElementsByClassName('lower-text');
      if (!day) {
        return;
      }
      const firstDaysXPosition = ['19', '19.65', '21', '22.5', '23.25', '24.9'];
      for (let m of Array.from(day)) {
        firstDaysXPosition.forEach((f) => {
          if (m.getAttribute('x') === f) {
            m.innerHTML = '01';
          }
        });
      }
    }
    if (this.state.viewMode === ViewMode.Month) {
      const months = this._svg.current?.getElementsByClassName('lower-text');
      if (!months) {
        return;
      }
      const yearBegining = [0];
      const ticks = this._svg.current?.getElementsByClassName('tick');
      for (let m of Array.from(months)) {
        if (m.innerHTML === 'Styczeń' || m.innerHTML === 'January') yearBegining.push(parseInt(m.getAttribute('x')!));
      }
      if (!ticks) {
        return;
      }

      for (let y of yearBegining) {
        for (let t of Array.from(ticks)) {
          const path = t.getAttribute('d')!;
          const x = parseInt(path.substring(path.indexOf('M') + 1, path.lastIndexOf('59')));
          if (y - 40 <= x && y + 10 >= x) {
            t.setAttribute('class', 'thick tick bold');
          }
          path.replace('v 48', 'v 400');
        }
      }
    }
  }

  changeGridLength() {
    const ticks = this._svg.current?.getElementsByClassName('tick');
    if (!ticks) {
      return;
    }

    for (let t of Array.from(ticks)) {
      const path = t.getAttribute('d')!;
      const heightPathValue = path.slice(path.indexOf('v'));
      const newPath = path.replace(heightPathValue, 'v 1150');
      t.setAttribute('d', newPath);
    }
    this._svg.current?.getElementsByClassName('today-highlight')?.[0]?.setAttribute('height', '100%');
  }

  componentDidUpdate() {
    if (this._gantt) {
      this._gantt.refresh(this.state.tasks);
      this._gantt.change_view_mode(this.state.viewMode);
      //   this.changeMonthsNames();
      this.changeCalendarLayout();
    }
    this.handleResize();
    // this.setSVGSize();
    this.changeGridLength();
  }

  componentDidMount() {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this._gantt = new MyGantt(this._svg.current, this.state.tasks, {
      on_click: this.props.onClick,
      on_view_change: this.props.onViewChange,
      header_height: 50,
      view_modes: ['Day', 'Month'],
      bar_corner_radius: 3,
      arrow_curve: 5,
      padding: 18,
      date_format: 'YYYY-MM-DD',
      column_width: 1,
      custom_popup_html: null,
      bar_height: 30,
      step: 1,
    });

    if (this._gantt) {
      this._gantt.change_view_mode(this.state.viewMode);
    }

    const midOfSvg = this._svg.current!.clientWidth * 0.5;
    this._target.current!.scrollLeft = midOfSvg;
    this.changeCalendarLayout();
    this.changeGridLength();
  }

  render() {
    return (
      <div ref={this._target} className={'h-full'}>
        <svg
          ref={this._svg}
          width="100%"
          preserveAspectRatio="xMinYMin slice"
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
        />
      </div>
    );
  }
}
